import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { MarketTag } from "../../components/MarketTag";
import { MarketTypeFilter } from "../../components/MarketTypeFilter";
import type { MarketTypeChoice } from "../../components/marketType";
import { api } from "../../api";
import { SortableTable } from "../../components/SortableTable";
import { CardCollection, ViewToggle, useViewMode } from "../../components/ViewToggle";
import { AuthorChip, Disclaimer, Stars } from "../../components/social";
import { dd, num, pct, timeAgo, tone } from "../../format";
import { Card, EmptyState, PageHead } from "../../shell/ui";
import { useSession } from "../../session";
import { directionColumn } from "../../components/direction";

interface BenchmarkRow {
  id: string;
  title: string | null;
  strategy_name: string;
  symbol: string;
  timeframe: string;
  market_type?: string;
  direction?: "long" | "short";
  total_return_pct: number;
  max_drawdown_pct: number;
  win_rate_pct: number;
  total_trades: number;
  sharpe: number | null;
  rating_avg: number;
  rating_count: number;
  comment_count: number;
  verify_count: number;
  verify_ok_count: number;
  published_at: string;
  author_username: string;
  author_name: string;
  author_avatar: string;
  author_simulated: boolean;
}

export function BenchmarksPage() {
  const navigate = useNavigate();
  const { user: me } = useSession();
  const isAdmin = me?.role === "admin";
  const [realPick, setReal] = useState(false);
  const real = isAdmin && realPick;
  const [marketType, setMarketType] = useState<MarketTypeChoice>("all");
  const [view, setView] = useViewMode("benchmarks", "details");
  const q = useQuery({
    queryKey: ["benchmarks", real, marketType],
    queryFn: () =>
      api<{ items: BenchmarkRow[]; total: number }>(
        `/hub/explore/benchmarks?limit=100${real ? "&real=1" : ""}${marketType !== "all" ? `&market=${marketType}` : ""}`,
      ),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="stack">
      <PageHead title="Benchmarks">
        <div className="row">
          <MarketTypeFilter value={marketType} onChange={setMarketType} stocks />
          {isAdmin && (
            <label className="checkbox">
              <input type="checkbox" checked={real} onChange={(e) => setReal(e.target.checked)} /> Real
              members only
            </label>
          )}
          <ViewToggle value={view} onChange={setView} />
        </div>
      </PageHead>
      <p className="muted">
        Published backtests. Every one can be re-run on the hub's engine to check it produces the same trades
        — click one and press “Re-run to verify”.
      </p>
      {view !== "details" ? (
        (q.data?.items.length ?? 0) === 0 && !q.isPending ? (
          <Card>
            <EmptyState icon="▦" title="No benchmarks published yet" />
          </Card>
        ) : (
          <CardCollection mode={view} busy={q.isFetching}>
            {(q.data?.items ?? []).map((r) => (
              <BenchmarkCard key={r.id} r={r} />
            ))}
          </CardCollection>
        )
      ) : (
        <Card flush>
          <SortableTable
            tableId="benchmarks"
            rows={q.data?.items ?? []}
            loading={q.isPending}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/r/${r.id}`)}
            initialSort={{ key: "when", direction: "desc" }}
            empty={<EmptyState icon="▦" title="No benchmarks published yet" />}
            columns={[
              {
                key: "title",
                label: "Benchmark",
                render: (r) => <strong>{r.title ?? r.strategy_name}</strong>,
                sortValue: (r) => r.title ?? r.strategy_name,
                firstDirection: "asc",
              },
              {
                key: "by",
                label: "By",
                render: (r) => (
                  <AuthorChip
                    username={r.author_username}
                    name={r.author_name}
                    avatar={r.author_avatar}
                    simulated={r.author_simulated}
                  />
                ),
                sortValue: (r) => r.author_name,
                firstDirection: "asc",
              },
              {
                key: "market",
                label: "Market",
                render: (r) => (
                  <span className="mono small">
                    {r.symbol} {r.timeframe} <MarketTag marketType={r.market_type} />
                  </span>
                ),
                sortValue: (r) => `${r.symbol} ${r.timeframe}`,
                firstDirection: "asc",
              },
              directionColumn(),
              {
                key: "ret",
                label: "Return",
                align: "right",
                render: (r) => <span className={tone(r.total_return_pct)}>{pct(r.total_return_pct)}</span>,
                sortValue: (r) => r.total_return_pct,
              },
              {
                key: "dd",
                label: "Max DD",
                align: "right",
                render: (r) => dd(r.max_drawdown_pct, 1),
                sortValue: (r) => r.max_drawdown_pct,
                firstDirection: "asc",
              },
              {
                key: "sharpe",
                label: "Sharpe",
                align: "right",
                render: (r) => num(r.sharpe),
                sortValue: (r) => r.sharpe,
              },
              {
                key: "trades",
                label: "Trades",
                align: "right",
                render: (r) => r.total_trades,
                sortValue: (r) => r.total_trades,
              },
              {
                key: "rating",
                label: "Rating",
                render: (r) => <Stars value={r.rating_avg} count={r.rating_count} size="sm" />,
                sortValue: (r) => (r.rating_count ? r.rating_avg : null),
              },
              {
                key: "verified",
                label: "Verified",
                align: "right",
                render: (r) => (r.verify_count ? `${r.verify_ok_count}/${r.verify_count} ✓` : "—"),
                sortValue: (r) => r.verify_ok_count,
              },
              {
                key: "when",
                label: "Published",
                align: "right",
                render: (r) => timeAgo(r.published_at),
                sortValue: (r) => new Date(r.published_at).getTime(),
              },
            ]}
          />
        </Card>
      )}
      <Disclaimer />
    </div>
  );
}

/** A published backtest as a card (grid and list views). */
function BenchmarkCard({ r }: { r: BenchmarkRow }) {
  return (
    <Link to={`/r/${r.id}`} className="card strategy-card">
      <div className="card__body">
        <div className="strategy-card__head">
          <h2>{r.title ?? r.strategy_name}</h2>
          <span className="row" style={{ gap: "0.3rem" }}>
            <span className="badge mono">
              {r.symbol} {r.timeframe}
            </span>
            <MarketTag marketType={r.market_type} />
          </span>
        </div>
        <AuthorChip
          username={r.author_username}
          name={r.author_name}
          avatar={r.author_avatar}
          simulated={r.author_simulated}
          suffix={<span className="muted small">· {timeAgo(r.published_at)}</span>}
        />
        <div className="strategy-card__stats collection-numbers">
          <span>
            <span className="stat__label">Return</span>
            <strong className={tone(r.total_return_pct)}>{pct(r.total_return_pct)}</strong>
          </span>
          <span>
            <span className="stat__label">Max DD</span>
            <strong>−{r.max_drawdown_pct.toFixed(1)}%</strong>
          </span>
          <span>
            <span className="stat__label">Trades</span>
            <strong>{r.total_trades}</strong>
          </span>
        </div>
        <div className="strategy-card__foot collection-aside">
          <Stars value={r.rating_avg} count={r.rating_count} size="sm" />
          <span className="muted small" title="Re-runs that reproduced the same trades">
            {r.verify_count ? `✓ verified ${r.verify_ok_count}/${r.verify_count}` : "not re-run yet"}
          </span>
        </div>
      </div>
    </Link>
  );
}
